(() => {
  const VERSION = "2026-08-14.3";
  const STORAGE_KEY = "c2g_attribution_v1";
  const SYNC_KEY = "c2g_attribution_cart_sync_v1";
  const CLICK_KEYS = ["gclid", "gbraid", "wbraid", "msclkid", "srsltid", "oppref"];
  const UTM_KEYS = ["source", "medium", "campaign", "content", "term"];
  const status = { version: VERSION, stage: "loaded", allowed: false, synced: false, error: "" };
  let syncInFlight = null;

  function report(stage) {
    status.stage = stage;
    document.documentElement.dataset.c2gAttribution = stage;
    document.documentElement.dataset.c2gAttributionVersion = VERSION;
  }

  report("loaded");

  function limit(value, max = 200) {
    return String(value || "").trim().slice(0, max);
  }

  function storageGet(storage, key) {
    try {
      return storage.getItem(key);
    } catch (_) {
      return null;
    }
  }

  function storageSet(storage, key, value) {
    try {
      storage.setItem(key, value);
      return true;
    } catch (_) {
      return false;
    }
  }

  function normalizedHost(value) {
    const host = limit(value, 255).toLowerCase().replace(/^www\./, "");
    if (!host) return "";
    if (host.includes("google.")) return "google";
    if (host.includes("bing.")) return "bing";
    if (host === "chatgpt.com" || host.endsWith(".chatgpt.com") || host === "openai.com") return "openai";
    return host;
  }

  function externalReferrer() {
    if (!document.referrer) return "";
    try {
      const referrer = new URL(document.referrer);
      if (referrer.hostname === window.location.hostname) return "";
      return normalizedHost(referrer.hostname);
    } catch (_) {
      return "";
    }
  }

  function googleClientId() {
    const cookie = document.cookie.split(";").map((part) => part.trim()).find((part) => part.startsWith("_ga="));
    if (!cookie) return "";
    const value = decodeURIComponent(cookie.slice(4));
    const parts = value.split(".");
    const candidate = parts.length >= 4 ? `${parts[parts.length - 2]}.${parts[parts.length - 1]}` : value;
    return /^\d+\.\d+$/.test(candidate) ? candidate : "";
  }

  function googleSessionId() {
    const cookie = document.cookie.split(";").map((part) => part.trim())
      .find((part) => part.startsWith("_ga_NBJLFZMQ54="));
    if (!cookie) return "";
    const value = decodeURIComponent(cookie.slice(cookie.indexOf("=") + 1));
    const modern = value.match(/(?:^|\.)s(\d+)(?:\$|\.|$)/);
    if (modern) return modern[1];
    const legacy = value.match(/^GS\d+\.\d+\.(\d+)(?:\.|$)/);
    return legacy ? legacy[1] : "";
  }

  function clickEvidence(params) {
    for (const key of CLICK_KEYS) {
      const value = limit(params.get(key), 400);
      if (value) return { type: key, id: value };
    }
    return { type: "", id: "" };
  }

  function inferredSource(params, referrer, click) {
    const utm = Object.fromEntries(UTM_KEYS.map((key) => [key, limit(params.get(`utm_${key}`), 160)]));
    if (!utm.source && ["gclid", "gbraid", "wbraid", "srsltid"].includes(click.type)) utm.source = "google";
    if (!utm.source && click.type === "msclkid") utm.source = "bing";
    if (!utm.source && click.type === "oppref") utm.source = "openai";
    if (!utm.medium && ["gclid", "gbraid", "wbraid", "msclkid"].includes(click.type)) utm.medium = "cpc";
    if (!utm.medium && click.type === "srsltid") utm.medium = "organic";
    if (!utm.medium && click.type === "oppref") utm.medium = "cpc";
    if (!utm.source && referrer) utm.source = referrer;
    if (!utm.medium && referrer) utm.medium = ["google", "bing"].includes(referrer) ? "organic" : "referral";
    if (!utm.source) utm.source = "(direct)";
    if (!utm.medium) utm.medium = "(none)";
    return utm;
  }

  function currentTouch() {
    const url = new URL(window.location.href);
    const referrer = externalReferrer();
    const click = clickEvidence(url.searchParams);
    const utm = inferredSource(url.searchParams, referrer, click);
    const hasMarketingParams = CLICK_KEYS.some((key) => url.searchParams.has(key)) ||
      UTM_KEYS.some((key) => url.searchParams.has(`utm_${key}`));
    return {
      source: utm.source,
      medium: utm.medium,
      campaign: utm.campaign,
      content: utm.content,
      term: utm.term,
      landing: limit(`${url.pathname}${url.hash || ""}`, 220),
      referrer,
      timestamp: new Date().toISOString(),
      click_type: click.type,
      click_id: click.id,
      qualified: hasMarketingParams || Boolean(referrer)
    };
  }

  function readState() {
    try {
      const parsed = JSON.parse(storageGet(window.localStorage, STORAGE_KEY) || "null");
      if (parsed && parsed.version === VERSION && parsed.first && parsed.last) return parsed;
    } catch (_) {}
    return null;
  }

  function updateState() {
    const touch = currentTouch();
    const previous = readState();
    const first = previous?.first || touch;
    const last = touch.qualified ? touch : previous?.last || touch;
    const click = touch.click_id
      ? touch
      : touch.qualified
        ? { click_type: "", click_id: "" }
        : previous?.click || touch;
    const next = { version: VERSION, first, last, click };
    storageSet(window.localStorage, STORAGE_KEY, JSON.stringify(next));
    return next;
  }

  function cartAttributes(state) {
    const attributes = {
      __c2g_attr_v: VERSION,
      __c2g_consent: "analytics+marketing",
      __c2g_ga_client_id: googleClientId(),
      __c2g_ga_session_id: googleSessionId(),
      __c2g_click_type: state.click.click_type,
      __c2g_click_id: state.click.click_id
    };
    for (const prefix of ["first", "last"]) {
      const touch = state[prefix];
      for (const key of ["source", "medium", "campaign", "content", "term", "landing", "referrer", "timestamp"]) {
        attributes[`__c2g_${prefix}_${key}`] = limit(touch[key], key === "landing" || key === "referrer" ? 220 : 160);
      }
    }
    return attributes;
  }

  function consentAllowed() {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) return null;
    try {
      return Boolean(privacy.analyticsProcessingAllowed() && privacy.marketingAllowed());
    } catch (_) {
      return null;
    }
  }

  async function syncOnce() {
    const allowed = consentAllowed();
    status.allowed = allowed === true;
    if (!allowed) {
      report(allowed === false ? "consent-blocked" : "privacy-pending");
      return;
    }
    const state = updateState();
    const attributes = cartAttributes(state);
    const fingerprint = JSON.stringify(attributes);
    if (storageGet(window.sessionStorage, SYNC_KEY) === fingerprint) {
      status.synced = true;
      report("synced");
      return;
    }
    const root = window.Shopify?.routes?.root || "/";
    try {
      const response = await fetch(`${root}cart/update.js`, {
        method: "POST",
        credentials: "same-origin",
        keepalive: true,
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ attributes })
      });
      if (!response.ok) throw new Error(`cart update ${response.status}`);
      storageSet(window.sessionStorage, SYNC_KEY, fingerprint);
      status.synced = true;
      status.error = "";
      report("synced");
    } catch (error) {
      status.error = limit(error, 160);
      report("error");
    }
  }

  function sync() {
    if (syncInFlight) return syncInFlight;
    syncInFlight = syncOnce().finally(() => {
      syncInFlight = null;
    });
    return syncInFlight;
  }

  function scheduleSync() {
    Promise.resolve().then(sync);
  }

  window.__c2gAttribution = Object.freeze({
    version: VERSION,
    status,
    refresh: sync,
    inspect: () => {
      const state = readState();
      if (!state) return null;
      return {
        version: state.version,
        first: { ...state.first, click_id: state.first.click_id ? "present" : "" },
        last: { ...state.last, click_id: state.last.click_id ? "present" : "" },
        click: { type: state.click.click_type, id: state.click.click_id ? "present" : "" },
        ga_client_id: googleClientId() ? "present" : "",
        ga_session_id: googleSessionId() ? "present" : ""
      };
    }
  });

  document.addEventListener("shopifyCustomerPrivacyApiLoaded", scheduleSync);
  document.addEventListener("visitorConsentCollected", scheduleSync);
  window.addEventListener("pageshow", scheduleSync);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleSync, { once: true });
  } else {
    scheduleSync();
  }
  window.setTimeout(scheduleSync, 1500);
})();
